import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

export interface ScrollableTab {
  id: string;
  label: string;
  count?: number;
  isActive?: boolean;
}

interface ScrollableTabBarProps {
  tabs: ScrollableTab[];
  activeTabId: string | null;
  onTabClick: (tabId: string) => void;
  onDeleteTab?: (tabId: string, tabLabel: string) => void;
  rightActions?: ReactNode;
  className?: string;
}

export const ScrollableTabBar: React.FC<ScrollableTabBarProps> = ({
  tabs,
  activeTabId,
  onTabClick,
  onDeleteTab,
  rightActions,
  className = '',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [tabs]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft + (dir === 'left' ? -200 : 200), behavior: 'smooth' });
  };

  if (!tabs.length) return null;

  return (
    <div
      className={className}
      style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}
    >
      {/* ── Scrollable tab strip ── */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        {/* Left arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            aria-label="Scroll left"
            style={{
              position: 'absolute', left: 0, zIndex: 10,
              width: 24, height: 24, borderRadius: '50%',
              background: '#1a2235', border: '1px solid #2a3a55',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
              boxShadow: '2px 0 8px rgba(0,0,0,0.4)',
              transform: 'translateX(-50%)',
              transition: 'background 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1f2d44'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#1a2235'; }}
          >
            <ChevronLeft size={13} color="#64748b" strokeWidth={2.5} />
          </button>
        )}

        {/* Tabs */}
        <div
          ref={scrollRef}
          style={{
            display: 'flex', gap: '0.375rem',
            overflowX: 'auto', padding: '0 2px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            flex: 1,
          }}
        >
          {tabs.map(tab => {
            const active = tab.id === activeTabId;
            return (
              <div
                key={tab.id}
                onClick={() => onTabClick(tab.id)}
                role="tab"
                aria-selected={active}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  borderRadius: 99,
                  background: active ? 'rgba(59,130,246,0.15)' : '#1a2235',
                  border: `1px solid ${active ? 'rgba(59,130,246,0.35)' : '#2a3a55'}`,
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.18s',
                  userSelect: 'none',
                }}
                onMouseEnter={e => {
                  if (!active) {
                    e.currentTarget.style.background = '#1f2d44';
                    e.currentTarget.style.borderColor = '#334155';
                  }
                }}
                onMouseLeave={e => {
                  if (!active) {
                    e.currentTarget.style.background = '#1a2235';
                    e.currentTarget.style.borderColor = '#2a3a55';
                  }
                }}
              >
                <span style={{
                  fontSize: '0.8125rem',
                  fontWeight: active ? 700 : 500,
                  color: active ? '#60a5fa' : '#64748b',
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.005em',
                  transition: 'color 0.18s',
                }}>
                  {tab.label}
                </span>

                {/* Count badge */}
                {tab.count !== undefined && (
                  <span style={{
                    fontSize: '0.67rem', fontWeight: 700,
                    padding: '0.1rem 0.4rem', borderRadius: 99,
                    background: active ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.06)',
                    color: active ? '#60a5fa' : '#334155',
                    minWidth: 18, textAlign: 'center',
                  }}>
                    {tab.count}
                  </span>
                )}

                {/* Delete button */}
                {onDeleteTab && (
                  <button
                    onClick={e => { e.stopPropagation(); onDeleteTab(tab.id, tab.label); }}
                    aria-label={`Delete ${tab.label}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: '50%',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: active ? '#3b82f6' : '#334155',
                      padding: 0, marginLeft: 1,
                      transition: 'color 0.18s, background 0.18s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.color = '#f87171';
                      e.currentTarget.style.background = 'rgba(239,68,68,0.12)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.color = active ? '#3b82f6' : '#334155';
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <X size={10} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Right arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            aria-label="Scroll right"
            style={{
              position: 'absolute', right: 0, zIndex: 10,
              width: 24, height: 24, borderRadius: '50%',
              background: '#1a2235', border: '1px solid #2a3a55',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
              boxShadow: '-2px 0 8px rgba(0,0,0,0.4)',
              transform: 'translateX(50%)',
              transition: 'background 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1f2d44'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#1a2235'; }}
          >
            <ChevronRight size={13} color="#64748b" strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* Right actions */}
      {rightActions && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          {rightActions}
        </div>
      )}
    </div>
  );
};
